import React from 'react';
import { Code2, Briefcase, ChevronRight, Sparkles } from 'lucide-react';
import { skills } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card } from './ui/card';

const SkillsNew = () => {
  return (
    <section id="skills" className="py-20 bg-[#f5f0e6] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#e0d0b8]/40 to-transparent blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#d9b896]/30 to-transparent blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className="font-handwriting text-3xl mb-2 gradient-text">What I bring to the table</p>
          <h2 className="text-4xl md:text-6xl font-bold text-[#2d2a26] mb-4">
            <span className="font-serif">Skills & </span>
            <span className="font-display gradient-text-animated">Capabilities</span>
          </h2>
          <p className="text-lg text-[#737373] max-w-2xl mx-auto">
            A blend of technical depth and business thinking
          </p>
        </div>

        <Tabs defaultValue="technical" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-white/70 mb-12 border border-[#e0d0b8] p-1 rounded-lg">
            <TabsTrigger
              value="technical"
              data-testid="skills-tab-technical"
              className="text-[#555555] data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#b88865] data-[state=active]:to-[#6b4226] data-[state=active]:text-white rounded-md transition-all duration-300"
            >
              <Code2 className="w-4 h-4 mr-2" />
              Technical
            </TabsTrigger>
            <TabsTrigger
              value="business"
              data-testid="skills-tab-business"
              className="text-[#555555] data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#b88865] data-[state=active]:to-[#6b4226] data-[state=active]:text-white rounded-md transition-all duration-300"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Business & Strategy
            </TabsTrigger>
          </TabsList>

          <TabsContent value="technical" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {skills.technical.map((skill, index) => (
                <Card
                  key={index}
                  data-testid={`skill-tech-${skill.name.replace(/\s+/g, '-').toLowerCase()}`}
                  className="bg-white/80 backdrop-blur-sm border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-300 p-6 group hover:shadow-lg hover:-translate-y-0.5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-[#2d2a26] group-hover:text-[#8b5a3c] transition-colors">
                      {skill.name}
                    </h3>
                    <span className="text-sm font-mono gradient-text font-bold">{skill.level}%</span>
                  </div>
                  <div className="relative h-2 bg-[#e8d9c0] rounded-full overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#b88865] via-[#8b5a3c] to-[#6b4226] rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="business" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {skills.business.map((skill, index) => (
                <Card
                  key={index}
                  data-testid={`skill-biz-${skill.name.replace(/\s+/g, '-').toLowerCase()}`}
                  className="bg-white/80 backdrop-blur-sm border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-300 p-6 group hover:shadow-lg hover:-translate-y-0.5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-[#2d2a26] group-hover:text-[#8b5a3c] transition-colors">
                      {skill.name}
                    </h3>
                    <span className="text-sm font-mono gradient-text font-bold">{skill.level}%</span>
                  </div>
                  <div className="relative h-2 bg-[#e8d9c0] rounded-full overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#b88865] via-[#8b5a3c] to-[#6b4226] rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Highlighted expertise */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card data-testid="capability-card-ai" className="bg-gradient-to-br from-white/90 to-[#b88865]/10 border-[#8b5a3c]/20 p-6 hover:border-[#8b5a3c]/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <div className="text-[#b88865] mb-3">
              <Sparkles className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 font-display gradient-text">AI & Intelligent Automation</h3>
            <p className="text-[#666666] text-sm mb-4 leading-relaxed">
              Proficient with modern LLMs (GPT-4, Claude 3.5, Gemini) and building AI-powered workflows using automation platforms (Zapier, Make, n8n). Experience with prompt engineering, API orchestration, and intelligent process automation at scale.
            </p>
            <div className="flex flex-wrap gap-2">
              {['GPT-4', 'Claude', 'Zapier', 'APIs'].map((t) => (
                <span key={t} className="text-xs px-2 py-1 bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] rounded-full border border-[#8b5a3c]/25">{t}</span>
              ))}
            </div>
          </Card>

          <Card data-testid="capability-card-product" className="bg-gradient-to-br from-white/90 to-[#8b5a3c]/10 border-[#8b5a3c]/20 p-6 hover:border-[#8b5a3c]/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <div className="text-[#8b5a3c] mb-3">
              <ChevronRight className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 font-display gradient-text">Product & Innovation</h3>
            <p className="text-[#666666] text-sm mb-4 leading-relaxed">
              Digital twins and synthetic modelling for requirement validation. Structured product thinking focused on user workflows, measurable outcomes, and continuous improvement through data-driven insights and iterative delivery.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Digital Twins', 'Product Thinking'].map((t) => (
                <span key={t} className="text-xs px-2 py-1 bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] rounded-full border border-[#8b5a3c]/25">{t}</span>
              ))}
            </div>
          </Card>

          <Card data-testid="capability-card-strategy" className="bg-gradient-to-br from-white/90 to-[#6b4226]/10 border-[#8b5a3c]/20 p-6 hover:border-[#8b5a3c]/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
            <div className="text-[#6b4226] mb-3">
              <ChevronRight className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 font-display gradient-text">Strategic Execution</h3>
            <p className="text-[#666666] text-sm mb-4 leading-relaxed">
              C-level stakeholder management with data-driven decision making. Cross-functional collaboration across product, technical, and commercial teams to deliver measurable business outcomes and operational efficiency gains.
            </p>
            <div className="flex flex-wrap gap-2">
              {['C-Level', 'Cross-Functional'].map((t) => (
                <span key={t} className="text-xs px-2 py-1 bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] rounded-full border border-[#8b5a3c]/25">{t}</span>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SkillsNew;
